import React, { useState, useEffect } from "react";

export type CheckboxProps = {
  /** determines text of the checkbox */
  text: string;
  /** determines checkbox is checked */
  checked: boolean;
  /**  */
  className?: string;
  /** */
  checkboxLabelClass?: string;
  /** determines if that checkbox is disabled */
  disabled?: boolean;
  /** determines if the checkbox containes error */
  errorMessage?: string;
  /** determines if the checkbox is indeterminate */
  indeterminate?: boolean;
  /** determines if the checkbox is styled exp. days of the week */
  styled?: boolean;
  /** determines if aria-checked is true/false/mixed used for inderminate */
  ariaChecked?: string;
  /**
   * onChange?: (lectedCheckbox: any) => void  <i>(event that is triggered when a checkbox is selected)</i>
   */
  onChange?: (selectedCheckbox: any) => void;
};

Checkbox.defaultProps = {
  disabled: false,
  error: false,
  checked: false,
  styled: false,
};

export function Checkbox({
  checked,
  text,
  disabled,
  errorMessage,
  onChange,
  indeterminate,
  styled,
  ariaChecked,
  ...props
}: CheckboxProps) {
  const [labelId, setLabelId] = useState("");
  const customClass = `${disabled ? "disabled" : ""} ${
    props.checkboxLabelClass || ""
  }`;
  const errorClass = `${errorMessage ? "error" : ""}`;
  const errorLabelId = errorMessage && labelId.concat("-error");
  const customAriaChecked: any = ariaChecked;
  const ariaLabel = props["aria-label"]
    ? props["aria-label"]  
    : props["data-testid"] ? props["data-testid"] : {};
  useEffect(() => {
    setLabelId(guid());
  }, []);

  // istanbul ignore next
  const onCheckboxChange = () => {
    onChange?.(!checked);
  };
  const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key == " ") {
      onCheckboxChange();
    }
  };
  const textSliced = (text || '').slice(0, 2);
  const contentA = <div className="checkbox-skin" />;
  const contentB = (
    <span
      className={`rad-chk-txt ${errorClass}`}
      dangerouslySetInnerHTML={{
        __html: text,
      }}
    />
  );
  const contentC = (
    <span
      className={`rad-chk-txt ${errorClass}`}
      dangerouslySetInnerHTML={{
        __html: textSliced,
      }}
    />
  );

  return indeterminate ? (
    <div
      className={`checkbox-selectall checkbox flex flex-items-top font-regular ${customClass}`}
      onClick={onCheckboxChange}
      onKeyPress={onKeyUp}

      role="checkbox"
      aria-checked={customAriaChecked}
      tabIndex={0}
    >
      {contentA}
      {contentB}
    </div>
  ) : styled ? (
    <>
      <label
        className="checkbox day-of-week inline-flex"
        data-testid={props['data-testid']}
        htmlFor={labelId}
      >
        <input
          className={props.className}
          id={labelId}
          onChange={onCheckboxChange}
          type="checkbox"
          checked={checked}
          aria-label={ariaLabel}
        />
        {contentA}
        {contentC}
      </label>
      <style global jsx>{`
      .form-row.day-of-week {
        display: flex;
        justify-content: space-between;
        max-width: 356px;
      }

      .checkbox.day-of-week {
        justify-content: center;
        min-height: 44px;
      }

      .checkbox.day-of-week input {
        left: -6px;
        top: 0;
      }

      .checkbox.day-of-week input + .checkbox-skin {
        align-self: center;
        background-color: transparent;
        border: none;
        box-shadow: 0 0 0 1px var(--white-on-dark, #454b52) inset;
        border-radius: 2em;
        height: 32px;
        max-height: 32px;
        width: 32px;
        min-width: 32px;
        font-size: 12px;
      }

      .checkbox.day-of-week input:hover + .checkbox-skin {
        box-shadow: 0 0 0 2px var(--white-on-dark, #0057b8) inset;
      }

      .checkbox.day-of-week input:checked + .checkbox-skin {
        background-color: var(--white-on-dark, #0057b8);
        box-shadow: 0 0 0 1px var(--white-on-dark, #0057b8) inset;
        color: white;
      }

      .checkbox.day-of-week input + .checkbox-skin:after {
        background-image: var(--day-of-week-svg, url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='16' height='16' fill='white' fill-opacity='0.01'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8 14.5C4.416 14.5 1.5 11.584 1.5 8C1.5 4.416 4.416 1.5 8 1.5C11.584 1.5 14.5 4.416 14.5 8C14.5 11.584 11.584 14.5 8 14.5Z' fill='%23f2fafd'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M11.148 5.313L11.855 6.02L6.833 11.041L4.145 8.354L4.852 7.646L6.833 9.627L11.148 5.313ZM8 14.5C4.416 14.5 1.5 11.584 1.5 8C1.5 4.416 4.416 1.5 8 1.5C11.584 1.5 14.5 4.416 14.5 8C14.5 11.584 11.584 14.5 8 14.5ZM8 0.5C3.864 0.5 0.5 3.864 0.5 8C0.5 12.136 3.864 15.5 8 15.5C12.136 15.5 15.5 12.136 15.5 8C15.5 3.864 12.136 0.5 8 0.5Z' fill='%230057b8'/%3E%3C/svg%3E")) !important;
        background-size: 16px;
        filter: none;
        top: -5px;
        right: -1px;
        height: 16px;
        width: 16px;
        opacity: 0;
        transform: scale(0.2);
        transition: transform .3s ease 0s, opacity .3s ease 0s;
      }

      .checkbox.day-of-week input:checked + .checkbox-skin:after {
        opacity: 1;
        transform: scale(1);
        transition: transform .3s ease 0s, opacity .3s ease 0s;
      }

      .checkbox.day-of-week input:checked:hover + .checkbox-skin {
        background-color: #007ae2;
        box-shadow: 0 0 0 1px #007ae2 inset;
      }

      .checkbox.day-of-week input ~ .rad-chk-txt {
        font-family: font-bold, sans-serif;
        color: var(--white-on-dark, #454b52);
        margin-left: 0;
        margin-bottom: 0;
        line-height: 1;
        top: 15px;
        position: absolute;
        width: 100%;
        text-align: center;
      }

      .checkbox.day-of-week input:checked ~ .rad-chk-txt {
        color: var(--cobalt-600-on-dark, #fff);
      }

      .checkbox.day-of-week input:checked:hover ~ .rad-chk-txt {
        color: #fff;
      }

      .checkbox.day-of-week input:hover ~ .rad-chk-txt {
        color: var(--white-on-dark, #0057b8);
      }

      .is-tabbing .checkbox.day-of-week input:focus + .checkbox-skin:before {
        border-radius: 0;
      }

      @media (max-width: 428px) {
        .form-row.day-of-week {
          max-width: 380px;
        }
      }

      @media (max-width: 320px) {
        .form-row.day-of-week {
          justify-content: center;
        }
        .checkbox.day-of-week {
          min-width: 44px;
        }
        .checkbox.day-of-week input {
          max-width: 32px;
        }
        .is-tabbing .checkbox.day-of-week input:focus + .checkbox-skin:before {
          outline-offset: 0;
        }
      }
    `}</style> 
    </>
  ) : (
    <div>
      <label
        className={`checkbox inline-flex flex-items-top font-regular ${customClass} ${errorClass}`}
        data-testid={props['data-testid']}
        htmlFor={labelId}
      >
        <input
          aria-invalid={errorMessage ? true : false}
          className={props.className}
          disabled={disabled}
          id={labelId}
          onChange={onCheckboxChange}
          required={!!errorMessage}
          type="checkbox"
          checked={checked}
          readOnly={!onChange ? true : false}
          aria-describedby={errorMessage ? errorLabelId : undefined}
          aria-label={ariaLabel}
          //{...ariaLabel}
        />
        {contentA}
        {contentB}
      </label>
      <Error errorMessage={errorMessage} labelId={errorLabelId} />
    </div>
  );
}

const Error = ({ errorMessage, labelId }) => {
  if (!errorMessage) return null;
  return (
    <div
      className="formfield-msg font-regular"
      aria-hidden={false}
      tabIndex={-1}
      id={labelId}
    >
      {errorMessage}
    </div>
  );
};

const guid = () => {
  return `${S4()}${S4()}-${S4()}-${S4()}-${S4()}-${S4()}${S4()}${S4()}`;
};

const S4 = () => {
  return Math.floor((1 + Math.random()) * 0x10000)
    .toString(16)
    .substring(1);
};
